import React, { useState } from "react";
import { Box, Typography, Button, IconButton, Link, Stack, CircularProgress, Snackbar, Alert } from "@mui/material";
import { Close as CloseIcon, CloudUpload as UploadIcon } from "@mui/icons-material";
import { postData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

function ImportEmployeesCSV({ onClose, refreshData }) {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const orgId = userData?.organization_id;
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success", // 'success' | 'error' | 'info' | 'warning'
    });

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && file.type === "text/csv") {
            setSelectedFile(file);
        } else {
            alert("Please upload a valid CSV file.");
        }
    };

    const handleRemoveFile = () => setSelectedFile(null);

    const handleUpload = async () => {
        if (!selectedFile || !orgId) return;

        setIsUploading(true);

        // Prepare FormData as per your CURL command
        const formData = new FormData();
        formData.append("organization_id", orgId);
        formData.append("file", selectedFile);

        try {
            // Using postData - ensure your axios instance handles FormData correctly
            const response = await postData(API_ENDPOINTS.uploadUsersByCsv, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.status === "SUCCESS") {
                showSnackbar("Employees imported successfully!", "success");
                if (refreshData) refreshData(); // Refresh the table list
                onClose();
            } else {
                showSnackbar(response?.message || "Failed to upload file.", "error");
            }
        } catch (error) {
            showSnackbar("An error occurred during upload. Please try again.", "error");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Upload a CSV file containing employee data. Each row should represent an employee.
            </Typography>

            <Link href="#" underline="hover" sx={{ fontSize: "0.875rem", display: "block", mb: 3 }}>
                Download sample CSV template <Typography component="span" color="text.secondary">(replace with actual path)</Typography>
            </Link>

            <Box
                sx={{
                    border: "2px dashed #ccc",
                    borderRadius: "8px",
                    p: 4,
                    textAlign: "center",
                    bgcolor: "#fafafa",
                    position: "relative",
                    mb: 4
                }}
            >
                {!selectedFile ? (
                    <>
                        <input
                            type="file"
                            accept=".csv"
                            style={{ display: "none" }}
                            id="csv-upload-input"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="csv-upload-input">
                            <Button component="span" startIcon={<UploadIcon />} sx={{ textTransform: 'none' }}>
                                Select CSV File
                            </Button>
                        </label>
                    </>
                ) : (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="subtitle1" fontWeight="600">
                            Selected File: {selectedFile.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            ({(selectedFile.size / 1024).toFixed(2)} KB)
                        </Typography>
                        <IconButton size="small" onClick={handleRemoveFile}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                )}
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!selectedFile}
                    onClick={handleUpload}
                    sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}
                >
                    {isUploading ? <CircularProgress size={24} color="inherit" /> : "Add Employees"}
                </Button>
            </Stack>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default ImportEmployeesCSV;